import { pushResortSelectionEvents } from '@/services/gtm-ecommerce.service';
import { resetBookingSession } from '@/services/orders.service';
import { useBookSkiPassStore, type ReferenceResponseData } from '@/store/book-ski-pass.store';
import { analyzeReferenceDomainActivity } from '@/utils/reference-domain-activity';
import type { ChangeEvent, FormEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const REFERENCE_API_URL = (import.meta.env.VITE_DISPATCHER_DATA_URL as string | undefined) || '';
const INVALID_REFERENCE_MESSAGE = 'Please enter a valid transfer booking reference to proceed';
const MISSING_REFERENCE_API_URL_MESSAGE = 'Configuration error: reference API URL is not set';

export const useBookSkiPassForm = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const hasHandledQueryAutoFlow = useRef(false);
  const stepOneFormFieldsValues = useBookSkiPassStore((state) => state.stepOneFormFieldsValues);
  const isLoading = useBookSkiPassStore((state) => state.isLoading);
  const error = useBookSkiPassStore((state) => state.error);
  const updateStepOneFormField = useBookSkiPassStore((state) => state.updateStepOneFormField);
  const setReferenceResponseData = useBookSkiPassStore((state) => state.setReferenceResponseData);
  const setBookingReferenceNumber = useBookSkiPassStore((state) => state.setBookingReferenceNumber);
  const setSelectedDomain = useBookSkiPassStore((state) => state.setSelectedDomain);
  const setHasAppliedReferenceToStepTwo = useBookSkiPassStore(
    (state) => state.setHasAppliedReferenceToStepTwo
  );
  const setResorts = useBookSkiPassStore((state) => state.setResorts);
  const setReferenceResortDomains = useBookSkiPassStore((state) => state.setReferenceResortDomains);
  const setLoading = useBookSkiPassStore((state) => state.setLoading);
  const setError = useBookSkiPassStore((state) => state.setError);
  const setCurrentStep = useBookSkiPassStore((state) => state.setCurrentStep);
  const resetStore = useBookSkiPassStore((state) => state.reset);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const field = event.target.name;

      if (field !== 'bookingReference' && field !== 'promoCode') {
        return;
      }

      setError(null);
      updateStepOneFormField(field, event.target.value);
    },
    [updateStepOneFormField, setError]
  );

  const fetchReferenceResponseData = useCallback(
    async (reference: string): Promise<boolean> => {
      if (!REFERENCE_API_URL) {
        setError(MISSING_REFERENCE_API_URL_MESSAGE);
        return false;
      }

      setLoading(true);
      setError(null);
      setBookingReferenceNumber(reference);
      setSelectedDomain(null);
      setHasAppliedReferenceToStepTwo(false);
      setResorts([]);
      setReferenceResortDomains(null);

      try {
        const response = await fetch(
          `${REFERENCE_API_URL}?reference=${encodeURIComponent(reference)}`
        );

        if (!response.ok) {
          setError(INVALID_REFERENCE_MESSAGE);
          return false;
        }

        const data: ReferenceResponseData = (await response.json()) as ReferenceResponseData;

        setReferenceResponseData(data);
        if (data.domains?.length) {
          setReferenceResortDomains(data.domains);
        }
        const { inactiveIds } = await analyzeReferenceDomainActivity(data);
        if (inactiveIds.length > 0) {
          useBookSkiPassStore.getState().purgeReferenceDomainIds(inactiveIds);
        }
        return true;
      } catch {
        setError(INVALID_REFERENCE_MESSAGE);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [
      setLoading,
      setError,
      setReferenceResponseData,
      setBookingReferenceNumber,
      setSelectedDomain,
      setHasAppliedReferenceToStepTwo,
      setResorts,
      setReferenceResortDomains,
    ]
  );

  const proceedToTripDetails = useCallback(
    async (reference: string, promoCode?: string) => {
      const trimmedReference = reference.trim();
      const trimmedPromoCode = promoCode?.trim() ?? '';

      resetStore();
      resetBookingSession();
      if (trimmedReference) {
        updateStepOneFormField('bookingReference', trimmedReference);
      }
      if (trimmedPromoCode) {
        updateStepOneFormField('promoCode', trimmedPromoCode);
      }

      if (!trimmedReference) {
        setCurrentStep(1);
        void navigate('/trip-details');
        return;
      }

      const success = await fetchReferenceResponseData(trimmedReference);
      if (!success) return;

      setCurrentStep(1);
      void navigate('/trip-details');
      pushResortSelectionEvents();
    },
    [
      resetStore,
      updateStepOneFormField,
      fetchReferenceResponseData,
      setCurrentStep,
      navigate,
    ]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const bookingReferenceFromQuery = searchParams.get('booking_ref')?.trim();
    const promoCodeFromQuery = searchParams.get('pc')?.trim();

    if (!stepOneFormFieldsValues.bookingReference.trim() && bookingReferenceFromQuery) {
      updateStepOneFormField('bookingReference', bookingReferenceFromQuery);
    }

    if (!stepOneFormFieldsValues.promoCode.trim() && promoCodeFromQuery) {
      updateStepOneFormField('promoCode', promoCodeFromQuery);
    }

    if (!bookingReferenceFromQuery || hasHandledQueryAutoFlow.current) {
      return;
    }

    const effectiveReference =
      stepOneFormFieldsValues.bookingReference.trim() || bookingReferenceFromQuery;

    if (!effectiveReference) {
      return;
    }

    const effectivePromoCode = stepOneFormFieldsValues.promoCode.trim() || promoCodeFromQuery;

    hasHandledQueryAutoFlow.current = true;
    searchParams.delete('booking_ref');
    searchParams.delete('pc');

    const cleanedSearch = searchParams.toString();
    void navigate(
      {
        pathname,
        search: cleanedSearch ? `?${cleanedSearch}` : '',
      },
      { replace: true }
    );

    void proceedToTripDetails(effectiveReference, effectivePromoCode);
  }, [
    search,
    pathname,
    stepOneFormFieldsValues.bookingReference,
    stepOneFormFieldsValues.promoCode,
    updateStepOneFormField,
    navigate,
    proceedToTripDetails,
  ]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      await proceedToTripDetails(
        stepOneFormFieldsValues.bookingReference,
        stepOneFormFieldsValues.promoCode
      );
    },
    [
      stepOneFormFieldsValues.bookingReference,
      stepOneFormFieldsValues.promoCode,
      proceedToTripDetails,
    ]
  );

  const fillTestRef = useCallback(() => {
    resetStore();
    resetBookingSession();
    updateStepOneFormField('bookingReference', 'A2A240096');
  }, [resetStore, updateStepOneFormField]);

  return {
    stepOneFormFieldsValues,
    isLoading,
    error,
    handleChange,
    handleSubmit,
    fillTestRef,
  };
};
